import type { Menu } from '@/services/manageService/menuService';
import type { UserInfo } from '@/store/admin/userInfo';

/** Fixed superadmin account id (same as UserUtil.getSuperUser). */
export const SUPERADMIN_USER_ID = 14773;

/** Superadmin role id; holders are not the superadmin account. */
export const SUPERADMIN_ROLE_ID = 1001;

/** System management role_name. */
export const SYS_MANAGEMENT_ROLE_NAME = 'sys-management';

/** sessionStorage: set on password login, consumed once by the warning dialog. */
export const CONTAINER_MEMORY_WARN_PENDING_KEY = 'containerMemoryWarnPending';

/**
 * True when the user is the protected superadmin account (not the superadmin role).
 *
 * @param user current session user
 * @returns whether username/id match the superadmin account
 */
export function isSuperadminAccount(user: UserInfo | null | undefined): boolean {
    if (!user) return false;
    if (user.userId === SUPERADMIN_USER_ID) return true;
    return String(user.username ?? '').toLowerCase() === 'superadmin';
}

/**
 * True when the user holds the superadmin role (role id 1001 or name superadmin).
 *
 * @param user current session user
 * @returns whether the superadmin role is bound
 */
export function hasSuperadminRole(user: UserInfo | null | undefined): boolean {
    if (!user) return false;
    if ((user.roles ?? []).includes(SUPERADMIN_ROLE_ID)) return true;
    return (user.roleNames ?? []).some(
        (roleName) => String(roleName).toLowerCase() === 'superadmin',
    );
}

/**
 * True when the user holds sys-management (session names, else Service Monitor menu).
 *
 * @param user current session user
 * @param menuTree optional sidebar tree when roleNames is missing on old sessions
 * @returns whether sys-management is present
 */
export function hasSysManagementRole(
    user: UserInfo | null | undefined,
    menuTree?: Menu[],
): boolean {
    if (!user) return false;
    const fromNames = (user.roleNames ?? []).some(
        (roleName) => String(roleName).toLowerCase() === SYS_MANAGEMENT_ROLE_NAME,
    );
    if (fromNames) return true;
    if ((user.roleNames ?? []).length > 0) return false;
    return menuTreeHasServiceMonitor(menuTree);
}

/**
 * Walk menu tree for Service Monitor realm or url.
 *
 * @param menus sidebar menus
 * @returns whether Service Monitor is present
 */
export function menuTreeHasServiceMonitor(menus: Menu[] | undefined): boolean {
    if (!menus?.length) return false;
    return menus.some((menu) => {
        const realm = String(menu.realm ?? '');
        const url = String(menu.realmUrl ?? '');
        if (realm === 'admin_service_monitor' || url.includes('service-monitor')) {
            return true;
        }
        return menuTreeHasServiceMonitor(menu.children);
    });
}

/**
 * Page + login-warning audience: superadmin account or sys-management, not superadmin role.
 *
 * @param user current session user
 * @param menuTree optional menus for old sessions without roleNames
 * @returns whether Service Monitor / actuator UI is allowed
 */
export function canAccessServiceMonitor(
    user: UserInfo | null | undefined,
    menuTree?: Menu[],
): boolean {
    if (isSuperadminAccount(user)) return true;
    if (hasSuperadminRole(user)) return false;
    return hasSysManagementRole(user, menuTree);
}

/** Survives React StrictMode remount in the same JS realm. */
let containerMemoryWarnCheckStarted = false;

/**
 * Mark that the next logged-in shell should run the memory warning once.
 */
export function markContainerMemoryWarnPending(): void {
    containerMemoryWarnCheckStarted = false;
    try {
        sessionStorage.setItem(CONTAINER_MEMORY_WARN_PENDING_KEY, '1');
    } catch {
        // ignore quota
    }
}

/**
 * True when sessionStorage still has the login flag (does not clear).
 *
 * @returns whether a login check is pending
 */
export function isContainerMemoryWarnPending(): boolean {
    try {
        return sessionStorage.getItem(CONTAINER_MEMORY_WARN_PENDING_KEY) === '1';
    } catch {
        return false;
    }
}

/**
 * Claim the one-shot login warning. Safe across StrictMode double-mount.
 *
 * @returns true when this caller should run the check
 */
export function tryStartContainerMemoryWarnCheck(): boolean {
    if (containerMemoryWarnCheckStarted) return false;
    if (!isContainerMemoryWarnPending()) return false;
    containerMemoryWarnCheckStarted = true;
    try {
        sessionStorage.removeItem(CONTAINER_MEMORY_WARN_PENDING_KEY);
    } catch {
        // ignore
    }
    return true;
}

/**
 * Drop the login warning flag (logout / ineligible user).
 */
export function clearContainerMemoryWarnPending(): void {
    containerMemoryWarnCheckStarted = false;
    try {
        sessionStorage.removeItem(CONTAINER_MEMORY_WARN_PENDING_KEY);
    } catch {
        // ignore
    }
}

/**
 * Container memory percent from used/limit, or null when stats are incomplete.
 *
 * @param used bytes used
 * @param limit bytes limit
 * @returns 0-100 rounded percent, or null
 */
export function containerMemoryPercent(
    used: number | null | undefined,
    limit: number | null | undefined,
): number | null {
    if (used == null || limit == null || !(limit > 0) || Number.isNaN(used) || Number.isNaN(limit)) {
        return null;
    }
    return Math.min(100, Math.round((used / limit) * 100));
}
